import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type PlatformStats, type PaginatedResponse, type HostWithStats, type HostDetail } from '../api/client';

export function usePlatformStats() {
  return useQuery<PlatformStats>({
    queryKey: ['admin', 'stats'],
    queryFn: () => api.admin.stats(),
    refetchInterval: 60_000,
  });
}

export function useAdminHosts(page = 1, limit = 20, search = '') {
  return useQuery<PaginatedResponse<HostWithStats>>({
    queryKey: ['admin', 'hosts', page, limit, search],
    queryFn: () => api.admin.listHosts(page, limit, search),
  });
}

export function useAdminHostDetail(hostId: string | undefined) {
  return useQuery<HostDetail>({
    queryKey: ['admin', 'host', hostId],
    queryFn: () => api.admin.getHost(hostId!),
    enabled: !!hostId,
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ hostId, password }: { hostId: string; password: string }) =>
      api.admin.resetPassword(hostId, password),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'host', variables.hostId] });
    },
  });
}

export function useToggleActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hostId: string) => api.admin.toggleActive(hostId),
    onSuccess: (_data, hostId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'hosts'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'host', hostId] });
    },
  });
}
